/**
 * LatestReviewCard — Card 3 of the P2-lite calm learner dashboard.
 *
 * "Your latest review" — shows the most recent AI review request and
 * its state. Replaces the old mix of floating chips + RequestAICard +
 * pending queue widgets that fought for attention on /learn.
 *
 * Trust + Continuity principle: answer the four questions every
 * persistent card must answer —
 *   • What is this?     "Your latest review"
 *   • Why is it here?   surfaces the one review the learner is
 *                       waiting on so they don't need to remember
 *                       its state across sessions
 *   • What happens?     tap → /lesson-review or /learn/ai-review-requests
 *   • Is it safe?       calm empty state when no review exists
 *
 * P1 visibility refetch ensures the state isn't stale after the
 * iPhone tab returns from background.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Loader2, CheckCircle2, Clock, AlertCircle, ChevronRight } from "lucide-react";
import useVisibilityRefetch from "../../hooks/useVisibilityRefetch";
import AsyncSurface from "../AsyncSurface";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_META = {
  awaiting_upload: {
    label: "Waiting for instructor upload",
    sub: "Your instructor will upload the video soon",
    icon: Clock,
    tone: "amber",
  },
  paid: {
    label: "Waiting for instructor upload",
    sub: "Your instructor will upload the video soon",
    icon: Clock,
    tone: "amber",
  },
  uploading: {
    label: "Upload in progress",
    sub: "Your video is being uploaded — almost there",
    icon: Loader2,
    tone: "indigo",
    spin: true,
  },
  analysing: {
    label: "AI is analysing your drive",
    sub: "Usually ready within a few minutes",
    icon: Loader2,
    tone: "indigo",
    spin: true,
  },
  processing: {
    label: "AI is analysing your drive",
    sub: "Usually ready within a few minutes",
    icon: Loader2,
    tone: "indigo",
    spin: true,
  },
  complete: {
    label: "Review ready",
    sub: "Tap to open your latest review",
    icon: CheckCircle2,
    tone: "emerald",
  },
  failed: {
    label: "Review couldn't complete",
    sub: "Tap for details — visible to your instructor in-app",
    icon: AlertCircle,
    tone: "rose",
  },
  refunded: {
    label: "Review refunded",
    sub: "Your credit is back — start a new one any time",
    icon: AlertCircle,
    tone: "slate",
  },
};

const TONE_CLASSES = {
  amber:   "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800",
  indigo:  "bg-indigo-50 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-800",
  emerald: "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800",
  rose:    "bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-800",
  slate:   "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700",
};

const ICON_TONE = {
  amber:   "text-amber-600 bg-amber-100 dark:bg-amber-900/30",
  indigo:  "text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30",
  emerald: "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30",
  rose:    "text-rose-600 bg-rose-100 dark:bg-rose-900/30",
  slate:   "text-slate-600 bg-slate-100 dark:bg-slate-800",
};

export default function LatestReviewCard() {
  const navigate = useNavigate();
  const [latest, setLatest] = useState(undefined);   // undefined = loading, null = no reviews
  const [loading, setLoading] = useState(true);
  // Phase 1.3 alive-contract migration (May 19, 2026) — the previous
  // catch silently set `latest = null` which made fetch failures
  // indistinguishable from "no reviews yet". A learner whose first
  // review was in flight could see "No reviews yet" purely because
  // the network blipped. Now error is tracked separately.
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      // Media quarantine: don't poll the quarantined endpoint —
      // it would 503 and flicker the card.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API}/api/lesson-review-requests/my-requests`,
        { withCredentials: true },
      );
      const items = data?.items || [];
      setLatest(items[0] || null);
    } catch (e) {
      // Truthful failure — preserve previous-known `latest` so the
      // refresh-failure case doesn't flicker the card to empty.
      setError(
        e?.response?.data?.detail ||
          "Couldn't load your latest review just now.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useVisibilityRefetch(load, { marker: "latest-review-card" });

  // Media quarantine — silent. No CTA into /lesson-review.
  if (!MEDIA_FEATURE_ENABLED) return null;

  if (loading && latest === undefined) {
    return (
      <AsyncSurface
        testid="latest-review-card"
        loading
        loadingLabel="Loading your latest review…"
      >
        {null}
      </AsyncSurface>
    );
  }

  // Truthful error state — only surfaces if we DON'T have a prior
  // successful result to fall back to.
  if (error && latest === undefined) {
    return (
      <AsyncSurface
        testid="latest-review-card"
        error={error}
        errorLabel={error}
        onRetry={load}
      >
        {null}
      </AsyncSurface>
    );
  }

  // ── Empty state — calm, not system-ish ──
  if (!latest) {
    return (
      <button
        data-testid="latest-review-card-empty"
        onClick={() => navigate("/lesson-review")}
        className="w-full text-left rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-4 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
      >
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
          Your latest review
        </p>
        <p className="text-base font-extrabold text-slate-900 dark:text-white mt-1">
          No reviews yet
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
          Start one any time
          <ChevronRight className="w-3.5 h-3.5" />
        </p>
      </button>
    );
  }

  const meta = STATE_META[latest.request_status] || {
    label: latest.request_status || "In progress",
    sub: "Tap for details",
    icon: Sparkles,
    tone: "slate",
  };
  const Icon = meta.icon;
  const isFailed = latest.request_status === "failed";
  const targetRoute = latest.lesson_review_id
    ? `/lesson-review/${latest.lesson_review_id}`
    : "/learn/ai-review-requests";

  return (
    <div className="space-y-2">
      <button
        data-testid="latest-review-card"
        data-state={latest.request_status}
        onClick={() => navigate(targetRoute)}
        className={`w-full text-left rounded-2xl border-2 ${TONE_CLASSES[meta.tone]} p-4 transition-colors`}
      >
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
          Your latest review
        </p>
        <div className="flex items-start gap-3 mt-1.5">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${ICON_TONE[meta.tone]}`}>
            <Icon className={`w-5 h-5 ${meta.spin ? "animate-spin" : ""}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-extrabold text-slate-900 dark:text-white truncate">
              {meta.label}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              {isFailed && latest.failure_reason
                ? latest.failure_reason
                : meta.sub}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
        </div>
      </button>

      {isFailed && (
        <FailedReviewActions
          requestId={latest.id}
          onChanged={load}
        />
      )}
    </div>
  );
}


/**
 * FailedReviewActions — Try again / Discard CTAs surfaced when a
 * review is in the `failed` state.
 *
 * May 14, 2026 — operator rule: every analysis must end in a visible
 * terminal state. POST /retry returns the request to the instructor
 * queue; POST /discard marks it permanently abandoned (still visible
 * in /history but not on /learn).
 */
function FailedReviewActions({ requestId, onChanged }) {
  const [busy, setBusy] = useState(null);

  const act = async (kind) => {
    if (busy || !requestId) return;
    setBusy(kind);
    try {
      await axios.post(
        `${API}/api/lesson-review-requests/${requestId}/${kind}`,
        {},
        { withCredentials: true },
      );
      if (onChanged) onChanged();
    } catch {
      // Silent — the card stays visible so the learner can retry.
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="failed-review-actions"
      className="flex items-center gap-2"
    >
      <button
        type="button"
        data-testid="failed-review-retry"
        onClick={() => act("retry")}
        disabled={!!busy}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-extrabold hover:bg-slate-800 dark:hover:bg-slate-100 disabled:opacity-50 transition"
      >
        {busy === "retry" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Try again
      </button>
      <button
        type="button"
        data-testid="failed-review-discard"
        onClick={() => act("discard")}
        disabled={!!busy}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition"
      >
        {busy === "discard" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Discard
      </button>
    </div>
  );
}
